import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, AlertCircle, Package, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  KPIGrid,
} from '@/components/common';
import { consumptionApi, ConsumptionRecord } from '@/api/services/consumption.api';
import { getErrorMessage } from '@/api/client';
import { formatDate } from '@/lib/utils';

/** @param embedded rendered as a tab inside Consumption; see the variance page. */
export default function ConsumptionApprovals({ embedded = false }: { embedded?: boolean }) {
  const navigate = useNavigate();
  const [pendingApprovals, setPendingApprovals] = useState<ConsumptionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Reject dialog
  const [rejectDialog, setRejectDialog] = useState<{
    open: boolean;
    record?: ConsumptionRecord;
  }>({ open: false });
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const fetchPendingApprovals = async () => {
    try {
      setLoading(true);
      try {
        const response = await consumptionApi.getPendingApprovals();
        setPendingApprovals(response.data);
      } catch (apiError) {
        // Fallback to mock data if API is not available
        console.log('API not available, using mock data');

        const createMockRecord = (partial: Partial<ConsumptionRecord>): ConsumptionRecord =>
          ({
            companyId: 'company-1',
            itemId: partial.item?.id || '',
            locationId: partial.location?.id || '',
            workOrderId: null,
            workOrder: null,
            approvedById: null,
            approvedBy: null,
            approvedAt: null,
            consumptionRate: null,
            rateUnit: null,
            baseMetric: null,
            certificateNumbers: [],
            requiresCertification: false,
            consumedById: partial.consumedBy?.id || '',
            createdAt: partial.consumedAt || new Date().toISOString(),
            updatedAt: partial.consumedAt || new Date().toISOString(),
            deletedAt: null,
            deletedBy: null,
            ...partial,
          }) as ConsumptionRecord;

        const mockApprovals: ConsumptionRecord[] = [
          createMockRecord({
            id: 'approval-1',
            consumedAt: new Date('2026-01-20T10:30:00').toISOString(),
            item: {
              id: 'item-1',
              name: 'Drilling Fluid',
              sku: 'DF-001',
              category: 'Fluids',
              unit: 'BBL',
            },
            location: { id: 'loc-1', name: 'Rig Site A', code: 'RSA' },
            plannedQuantity: 100,
            quantity: 115,
            unit: 'BBL',
            variancePercentage: 15.0,
            varianceReason: 'FIELD_CONDITION_CHANGE',
            unitCost: 500,
            totalCost: 57500,
            jobName: 'Well-001',
            jobId: 'WELL-001',
            operationPhase: 'DRILLING',
            approvalStatus: 'PENDING',
            requiresApproval: true,
            consumedBy: { id: 'user-1', fullName: 'John Smith', email: 'john.smith@example.com' },
            notes:
              'Lost circulation in formation, required extra drilling fluid to maintain pressure',
          }),
          createMockRecord({
            id: 'approval-2',
            consumedAt: new Date('2026-01-19T14:15:00').toISOString(),
            item: {
              id: 'item-5',
              name: 'Cement Slurry',
              sku: 'CS-001',
              category: 'Cement',
              unit: 'BBL',
            },
            location: { id: 'loc-2', name: 'Rig Site B', code: 'RSB' },
            plannedQuantity: 150,
            quantity: 172,
            unit: 'BBL',
            variancePercentage: 14.67,
            varianceReason: 'FIELD_CONDITION_CHANGE',
            unitCost: 450,
            totalCost: 77400,
            jobName: 'Well-002',
            jobId: 'WELL-002',
            operationPhase: 'COMPLETION',
            approvalStatus: 'PENDING',
            requiresApproval: true,
            consumedBy: {
              id: 'user-2',
              fullName: 'Sarah Johnson',
              email: 'sarah.johnson@example.com',
            },
            notes:
              'Larger wellbore volume than anticipated, additional cement required for proper seal',
          }),
          createMockRecord({
            id: 'approval-3',
            consumedAt: new Date('2026-01-18T09:45:00').toISOString(),
            item: {
              id: 'item-3',
              name: 'Drill Bits (PDC)',
              sku: 'DB-001',
              category: 'Tools',
              unit: 'EA',
            },
            location: { id: 'loc-1', name: 'Rig Site A', code: 'RSA' },
            plannedQuantity: 5,
            quantity: 6,
            unit: 'EA',
            variancePercentage: 20.0,
            varianceReason: 'EQUIPMENT_ISSUE',
            unitCost: 4500,
            totalCost: 27000,
            jobName: 'Well-001',
            jobId: 'WELL-001',
            operationPhase: 'DRILLING',
            approvalStatus: 'PENDING',
            requiresApproval: true,
            consumedBy: { id: 'user-1', fullName: 'John Smith', email: 'john.smith@example.com' },
            notes:
              'Encountered unexpected hard formation, damaged 1 bit prematurely. Required replacement.',
          }),
          createMockRecord({
            id: 'approval-4',
            consumedAt: new Date('2026-01-17T16:20:00').toISOString(),
            item: {
              id: 'item-7',
              name: 'Drilling Mud Additives',
              sku: 'DMA-001',
              category: 'Additives',
              unit: 'LB',
            },
            location: { id: 'loc-3', name: 'Rig Site C', code: 'RSC' },
            plannedQuantity: 80,
            quantity: 92,
            unit: 'LB',
            variancePercentage: 15.0,
            varianceReason: 'FIELD_CONDITION_CHANGE',
            unitCost: 45,
            totalCost: 4140,
            jobName: 'Well-003',
            jobId: 'WELL-003',
            operationPhase: 'DRILLING',
            approvalStatus: 'PENDING',
            requiresApproval: true,
            consumedBy: { id: 'user-3', fullName: 'Mike Davis', email: 'mike.davis@example.com' },
            notes: 'Mud weight adjustment needed due to high pressure zone',
          }),
          createMockRecord({
            id: 'approval-5',
            consumedAt: new Date('2026-01-16T11:10:00').toISOString(),
            item: {
              id: 'item-8',
              name: 'Wireline Tools',
              sku: 'WT-001',
              category: 'Tools',
              unit: 'EA',
            },
            location: { id: 'loc-2', name: 'Rig Site B', code: 'RSB' },
            plannedQuantity: 2,
            quantity: 3,
            unit: 'EA',
            variancePercentage: 50.0,
            varianceReason: 'EQUIPMENT_ISSUE',
            unitCost: 12000,
            totalCost: 36000,
            jobName: 'Well-002',
            jobId: 'WELL-002',
            operationPhase: 'COMPLETION',
            approvalStatus: 'PENDING',
            requiresApproval: true,
            consumedBy: {
              id: 'user-2',
              fullName: 'Sarah Johnson',
              email: 'sarah.johnson@example.com',
            },
            notes: 'Tool stuck in hole, required backup tool deployment for completion',
          }),
        ];
        setPendingApprovals(mockApprovals);
        toast.info('Showing mock approval data (API not available)');
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  const handleApprove = async (id: string) => {
    try {
      await consumptionApi.approve(id);
      toast.success('Consumption approved successfully');
      setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
      fetchPendingApprovals();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one record to approve');
      return;
    }

    try {
      const promises = selectedIds.map((id) => consumptionApi.approve(id));
      await Promise.all(promises);
      toast.success(`${selectedIds.length} consumption records approved successfully`);
      setSelectedIds([]);
      fetchPendingApprovals();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.record) return;

    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      setRejecting(true);
      await consumptionApi.reject(rejectDialog.record.id, rejectReason);
      toast.success('Consumption rejected successfully');
      setRejectDialog({ open: false });
      setRejectReason('');
      setSelectedIds((prev) => prev.filter((id) => id !== rejectDialog.record?.id));
      fetchPendingApprovals();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setRejecting(false);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
    }
  };

  const columns: Column<ConsumptionRecord>[] = [
    {
      key: 'select',
      header: '',
      render: (row: ConsumptionRecord) => (
        <Checkbox
          checked={selectedIds.includes(row.id)}
          onCheckedChange={(checked) => handleSelectOne(row.id, checked as boolean)}
        />
      ),
    },
    {
      key: 'consumedAt',
      header: 'Date',
      sortable: true,
      render: (row: ConsumptionRecord) => <span>{formatDate(row.consumedAt)}</span>,
    },
    {
      key: 'item',
      header: 'Item',
      render: (row: ConsumptionRecord) => (
        <div>
          <p className="font-medium">{row.item.name}</p>
          <p className="text-xs text-muted-foreground">{row.item.sku}</p>
        </div>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (row: ConsumptionRecord) => <span>{row.location.name}</span>,
    },
    {
      key: 'jobInfo',
      header: 'Job/Well',
      render: (row: ConsumptionRecord) => {
        if (!row.jobName && !row.jobId) return <span className="text-muted-foreground">-</span>;
        return (
          <div>
            {row.jobName && <p className="font-medium text-sm">{row.jobName}</p>}
            {row.jobId && <p className="text-xs text-muted-foreground">{row.jobId}</p>}
          </div>
        );
      },
    },
    {
      key: 'variance',
      header: 'Variance',
      render: (row: ConsumptionRecord) => (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Planned: {row.plannedQuantity} {row.unit}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Actual: {row.quantity} {row.unit}
            </span>
          </div>
          <StatusBadge
            status={row.variancePercentage && row.variancePercentage > 0 ? 'overdue' : 'completed'}
            label={`${row.variancePercentage && row.variancePercentage > 0 ? '+' : ''}${row.variancePercentage?.toFixed(1)}%`}
          />
        </div>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (row: ConsumptionRecord) => (
        <span className="text-sm">{row.varianceReason || '-'}</span>
      ),
    },
    {
      key: 'consumedBy',
      header: 'Technician',
      render: (row: ConsumptionRecord) => (
        <div>
          <p className="text-sm font-medium">{row.consumedBy.fullName}</p>
          <p className="text-xs text-muted-foreground">{row.consumedBy.email}</p>
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row: ConsumptionRecord) => (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate(`/consumption/${row.id}`)}>
            View Details
          </Button>
          <Button size="sm" variant="default" onClick={() => handleApprove(row.id)}>
            <CheckCircle className="mr-1 h-3 w-3" />
            Approve
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setRejectDialog({ open: true, record: row })}
          >
            <XCircle className="mr-1 h-3 w-3" />
            Reject
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      {embedded ? (
        <div className="flex items-center justify-end gap-2">
          <Button onClick={handleBulkApprove} disabled={selectedIds.length === 0}>
            <CheckCircle className="mr-2 h-4 w-4" />
            Approve Selected ({selectedIds.length})
          </Button>
        </div>
      ) : (
        <PageHeader
          title="Consumption Approvals"
          description="Consumption records exceeding ten percent variance against plan, held for a decision."
          actions={
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-base px-3 py-1">
                {pendingApprovals.length} Pending
              </Badge>
              <Button onClick={handleBulkApprove} disabled={selectedIds.length === 0}>
                <CheckCircle className="mr-2 h-4 w-4" />
                Approve Selected ({selectedIds.length})
              </Button>
            </div>
          }
        />
      )}

      {/* Summary Metrics */}
      {pendingApprovals.length > 0 && (
        <KPIGrid columns={4}>
          <MetricCard
            label="Total Pending"
            value={pendingApprovals.length}
            icon={<AlertCircle className="h-5 w-5" />}
            trend={{ direction: 'neutral', value: 'Requires attention' }}
          />
          <MetricCard
            label="Unique Items"
            value={new Set(pendingApprovals.map((r) => r.itemId)).size}
            icon={<Package className="h-5 w-5" />}
            trend={{ direction: 'neutral', value: 'Items awaiting approval' }}
          />
          <MetricCard
            label="Unique Locations"
            value={new Set(pendingApprovals.map((r) => r.locationId)).size}
            icon={<MapPin className="h-5 w-5" />}
            trend={{ direction: 'neutral', value: 'Sites requiring review' }}
          />
          <MetricCard
            label="Avg Variance"
            value={`${(pendingApprovals.reduce((sum, r) => sum + (r.variancePercentage || 0), 0) / pendingApprovals.length).toFixed(1)}%`}
            icon={<CheckCircle className="h-5 w-5" />}
            trend={{ direction: 'up', value: 'Above threshold' }}
          />
        </KPIGrid>
      )}

      {/* Data Table */}
      {pendingApprovals.length > 0 ? (
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground">Pending Approvals</h3>
            <CardDescription>
              Select records and use bulk actions or approve/reject individually
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              onRowClick={(row) => navigate(`/consumption/${row.id}`)}
              columns={columns}
              data={pendingApprovals}
              loading={loading}
              pagination
              exportable
              columnToggle
              searchable
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle className="h-16 w-16 text-status-success mb-4" />
            <p className="text-xl font-medium mb-2">All caught up!</p>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              There are no pending consumption approvals at this time. Records will appear here when
              consumption variances exceed 10%.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Consumption</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this consumption record.
            </DialogDescription>
          </DialogHeader>

          {rejectDialog.record && (
            <div className="space-y-4">
              <div className="p-4 border rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Item:</span>
                  <span className="text-sm">{rejectDialog.record.item.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Planned:</span>
                  <span className="text-sm">
                    {rejectDialog.record.plannedQuantity} {rejectDialog.record.unit}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Actual:</span>
                  <span className="text-sm">
                    {rejectDialog.record.quantity} {rejectDialog.record.unit}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Variance:</span>
                  <StatusBadge
                    status={
                      rejectDialog.record.variancePercentage &&
                      rejectDialog.record.variancePercentage > 0
                        ? 'overdue'
                        : 'completed'
                    }
                    label={`${
                      rejectDialog.record.variancePercentage &&
                      rejectDialog.record.variancePercentage > 0
                        ? '+'
                        : ''
                    }${rejectDialog.record.variancePercentage?.toFixed(1)}%`}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rejectReason">Rejection Reason *</Label>
                <Textarea
                  id="rejectReason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g., Incorrect quantity - recount required, Missing documentation, etc."
                  rows={4}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={rejecting}>
              {rejecting ? 'Rejecting...' : 'Confirm Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      {/* A record is inspected here, not acted on, so this is a sheet rather
          than a modal — the same shape Consumption Tracking already used for
          the same record. A dialog blocks the page behind it, which is right
          for a decision and wrong for reading. */}
      {/* The detail panel lived here, and as a sheet on Consumption
          Tracking — the same record in two shapes. It is a page now, at
          /consumption/:id, which an approval hand-off can be sent as a link. */}
    </div>
  );
}
